import { createWebHistory, createRouter, Route } from 'vue-router'
import { authStore } from './store/modules/auth.store'
import i18n from './localization/i18n'
import { BrowserDetect } from './services/detect-browser.js'
import { AliasType, MedicalType } from './models/enums'
import { startRouteLoading, stopRouteLoading } from './services/route-loading'


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // redirect to default app - medical
      path: '/',
      name: 'root',
      redirect: '/advanced-search',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/Login.vue'),
      meta: {
        title: i18n.global.t('route_login'),
      }
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('./views/CheckoutForm.vue'),
      meta: {
        title: 'Checkout',
      }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('./views/Register.vue'),
      meta: {
        title: i18n.global.t('route_register'),
      },
    },
    {
      path: '/forgotPassword',
      name: 'forgotPassword',
      component: () => import('./views/ForgotPwd.vue'),
      meta: {
        title: i18n.global.t('route_forgot_pwd'),
      },
    },
    {
      path: '/resetPassword',
      name: 'resetPassword',
      component: () => import('./views/ResetPwd.vue'),
      meta: {
        title: i18n.global.t('route_reset_pwd'),
      },
    },
    {
      path: '/confirmEmail',
      name: 'confirmEmail',
      component: () => import('./views/ConfirmEmail.vue'),
      meta: {
        title: i18n.global.t('route_confirm_email'),
      },
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('./views/Error.vue'),
      meta: {
        title: i18n.global.t('error_txt'),
      },
    },
    {
      path: '/advanced-search',
      name: 'advanced-search',
      component: () => import('./views/medical/Rates.vue'),
      meta: {
        title: 'Rates',
        medicalType: MedicalType.RATES
      },
    },
    {
      // redirect medical to advanced-search
      path: '/medical',
      redirect: (to) => {
        return {
          name: 'advanced-search',
          query: to.query,
          params: to.params
        }
      },
    },
    {
      path: '/providers',
      name: 'providers',
      component: () => import('./views/medical/Providers.vue'),
      meta: {
        title: 'Providers',
        medicalType: MedicalType.PROVIDERS
      },
    },
    {
      // redirect medical-providers to providers
      path: '/medical-providers',
      redirect: (to) => {
        return {
          name: 'providers',
          query: to.query,
          params: to.params
        }
      },
    },
    {
      path: '/benchmarks',
      name: 'benchmarks',
      component: () => import('./views/medical/Reports.vue'),
      meta: {
        title: 'Distribution',
        medicalType: MedicalType.REPORT,
        requiresAuth: true,
        roles: ['Admin'],
      },
    },
    {
      // redirect medical-reports to benchmarks
      path: '/medical-reports',
      redirect: (to) => {
        return {
          name: 'benchmarks',
          query: to.query,
          params: to.params
        }
      },
    },
    {
      path: '/networks',
      name: 'networks',
      component: () => import('./views/medical/Networks.vue'),
      meta: {
        title: 'Network',
        medicalType: MedicalType.NETWORK,
        requiresAuth: true,
        roles: ['Org User', 'Org Admin', 'Admin'],
      },
    },
    {
      // redirect medical-networks to networks
      path: '/medical-networks',
      redirect: (to) => {
        return {
          name: 'networks',
          query: to.query,
          params: to.params
        }
      },
    },
    {
      path: '/provider-overlap',
      name: 'provider-overlap',
      component: () => import('./views/medical/ProviderOverlap.vue'),
      meta: {
        title: 'Provider Overlap',
        medicalType: MedicalType.PROVIDER_OVERLAP,
        requiresAuth: true,
        roles: ['Org User', 'Org Admin', 'Admin'],
      },
    },
    {
      path: '/compare',
      name: 'compare',
      component: () => import('./views/medical/Compare.vue'),
      meta: {
        title: 'Comparison',
        medicalType: MedicalType.COMPARE,
        requiresAuth: true,
        roles: ['Org User', 'Org Admin', 'Admin'],
      },
    },
    {
      path: '/proforma',
      name: 'proforma',
      component: () => import('./views/medical/Compare.vue'),
      meta: {
        title: 'Pro Forma',
        medicalType: MedicalType.PROFORMA,
        requiresAuth: true,
        roles: ['Org User', 'Org Admin', 'Admin'],
      },
    },
    {
      path: '/settings',
      name: 'settings',
      redirect: '/settings/downloads',
      component: () => import('./views/medical/settings/Settings.vue'),
      meta: {
        title: 'Settings',
        requiresAuth: true,
        roles: ['Org User', 'Org Admin', 'Admin'],
      },
      children: [
        {
          path: '/settings/users',
          name: 'users',
          component: () => import('./views/medical/settings/UserSettings.vue'),
          meta: {
            title: 'Users',
          },
        },
        {
          path: '/settings/subscriptions',
          name: 'subscriptions',
          component: () => import('./views/medical/settings/Subscriptions.vue'),
          meta: {
            title: 'Subscriptions',
          },
        },
        {
          path: '/settings/invoices',
          name: 'invoices',
          component: () => import('./views/medical/settings/Invoices.vue'),
          meta: {
            title: 'Invoices',
          },
        },
        {
          path: '/settings/downloads',
          name: 'downloads',
          component: () => import('./views/medical/settings/MyDownloadsSettings.vue'),
          meta: {
            title: 'Downloads',
          },
        },
        {
          path: '/settings/data-reporting',
          name: 'data-reporting',
          component: () => import('./views/medical/settings/DataReporting.vue'),
          meta: {
            title: 'Report Payer Data Issues',
          },
        },
      ]
    },
    {
      // redirect mydownloads to downloads
      path: '/medical/settings/mydownloads',
      redirect: (to) => {
        return {
          name: 'downloads',
          query: to.query,
          params: to.params
        }
      },
    },
    {
      path: '/admin',
      name: 'admin',
      redirect: 'admin/users',
      component: () => import('./views/medical/admin/Admin.vue'),
      meta: {
        title: i18n.global.t('route_admin'),
        requiresAuth: true,
        roles: ['Admin'],
      },
      children: [
        {
          path: '/admin/users',
          name: 'admin-users',
          component: () => import('./views/medical/admin/AdminUsers.vue'),
          meta: {
            title: 'All Users',
          },
          
        },
        {
          path: '/admin/organizations',
          name: 'admin-organizations',
          component: () => import('./views/medical/admin/AdminOrganizations.vue'),
          meta: {
            title: 'All Organizations',
          },
        },
        {
          path: '/admin/payer-aliases',
          name: 'admin-payer-aliases',
          component: () => import('./views/medical/admin/AdminAliases.vue'),
          meta: {
            title: 'Payer Aliases',
            aliasType: AliasType.PAYERS
          }
        },
        {
          path: '/admin/service-code-aliases',
          name: 'admin-service-code-aliases',
          component: () => import('./views/medical/admin/AdminAliases.vue'),
          meta: {
            title: 'Place of Service Aliases',
            aliasType: AliasType.SERVICE_CODES
          }
        },
        {
          path: '/admin/active-codes',
          name: 'admin-active-codes',
          component: () => import('./views/medical/admin/AdminActiveCodes.vue'),
          meta: {
            title: 'Active Codes',
          },
        },
        {
          path: '/admin/subscriptions',
          name: 'admin-subscriptions',
          component: () => import('./views/medical/admin/AdminSubscriptions.vue'),
          meta: {
            title: 'All Subscriptions',
          },
        },
        {
          path: '/admin/dashboard',
          name: 'admin-dashboard',
          component: () => import('./views/medical/admin/AdminDashboard.vue'),
          meta: {
            title: 'Admin Dashboard',
          },
        },
        {
          path: '/admin/emails',
          name: 'admin-emails',
          component: () => import('./views/medical/admin/AdminEmails.vue'),
          meta: {
            title: 'Emails',
          },
        },
        {
          path: '/admin/messages',
          name: 'admin-messages',
          component: () => import('./views/medical/admin/AdminMessages.vue'),
          meta: {
            title: 'Messages',
          },
        },
        {
          path: '/admin/rate-discovery',
          name: 'admin-rate-discovery',
          component: () => import('./views/medical/admin/AdminRateDiscovery.vue'),
          meta: {
            title: 'Rate Discovery',
          },
        },
        {
          path: '/admin/rate-analysis',
          name: 'admin-rate-analysis',
          component: () => import('./views/medical/admin/AdminRateAnalysis.vue'),
          meta: {
            title: 'Rate Analysis',
          },
        },
        {
          path: '/admin/invoices',
          name: 'admin-invoices',
          component: () => import('./views/medical/admin/AdminInvoices.vue'),
          meta: {
            title: 'Invoices',
          },
        },
        {
          path: '/admin/app-settings',
          name: 'admin-app-settings',
          component: () => import('./views/admin/AdminAppSettings.vue'),
          meta: {
            title: 'App Settings',
          },
        },
        {
          path: '/settings/admin-data-reporting',
          name: 'admin-data-reporting',
          component: () => import('./views/medical/settings/DataReporting.vue'),
          meta: {
            title: 'Reported Payer Data Issues',
          },
        },
      ]
    },
    {
      // redirect unmatched routes to root
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})


router.beforeEach(() => {
  startRouteLoading()
})

router.onError(() => {
  stopRouteLoading()
})

// check routes for access rights
router.beforeEach((to: Route) => {
  // check if supported browser
  if (BrowserDetect().isIEorEdge) {
    return to.path !== '/error' ? 'error' : true
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    // check parent routes for meta too
    const roles: string[] = to.matched.find(m => m.meta.roles)!.meta.roles
    const role = authStore.decodedToken ? authStore.decodedToken.role : null
    const authorized = role ? roles.includes(role as string) : false

    if (!authorized) {
      console.warn('Unauthorized')
      authStore.logout()
      return { name: 'login', query: { ...to.query, redirect: to.name! } }
    }

    return true
  }

  // jump to root page if user is already logged in and was not redirected to login
  if (authStore.isLoggedIn && to.name === 'login' && to.query.redirect == null) {
    return { name: 'root' }
  }

  return true
})

router.afterEach((to) => {
  stopRouteLoading()
  document.title = to.meta.title
    ? `${to.meta.title} | HexIQ`
    : 'HexIQ'
})

export default router
